import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  BarChart3, UserPlus, Upload, Download, List, Pencil, 
  Sparkles, Loader2, CheckCircle, Clock, Tag, Users, QrCode, Printer, Trash2, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import { beneficiariesAPI, redemptionsAPI, vendorAPI } from '../services/api';
import EditBeneficiaryModal from './EditBeneficiaryModal';
import VendorAssignmentSelect from './VendorAssignmentSelect';
import QRCodeHighQuality from './QRCodeHighQuality';
import QRCodePrintable from './QRCodePrintable';

const AdminDashboard = ({ userId, role, currentProject, projects }) => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [newBeneficiary, setNewBeneficiary] = useState(null);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const [expandedBeneficiaryId, setExpandedBeneficiaryId] = useState(null);
  const [redemptionDetails, setRedemptionDetails] = useState({});
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importStatus, setImportStatus] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDetails, setImportDetails] = useState(null);
  const [showProjectSelection, setShowProjectSelection] = useState(false);
  const [selectedImportProject, setSelectedImportProject] = useState(null);
  const [reportNarrative, setReportNarrative] = useState('');
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRBeneficiary, setSelectedQRBeneficiary] = useState(null);
  
  // Search and pagination states
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Form states
  const [name, setName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [couponCount, setCouponCount] = useState(1);
  const [couponValue, setCouponValue] = useState(0);
  const [assignedVendors, setAssignedVendors] = useState(['ALL VENDORS']);
  const [selectedProjects, setSelectedProjects] = useState([]);

  // Vendor list state - fetched from database
  const [vendorList, setVendorList] = useState(['ALL VENDORS']);

  // Fetch vendors from database
  useEffect(() => {
    fetchVendors();
    
    // Refresh vendors periodically to catch newly added vendors
    const vendorRefreshInterval = setInterval(() => {
      fetchVendors();
    }, 30000); // Refresh every 30 seconds
    
    // Refresh vendors when window regains focus (user returns from another page)
    const handleFocus = () => {
      fetchVendors();
    };
    
    // Refresh vendors when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchVendors();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      clearInterval(vendorRefreshInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch beneficiaries when component mounts or project changes
  useEffect(() => {
    if (currentProject) {
      fetchBeneficiaries();
    }
  }, [currentProject?.id]); // Only depend on project ID to avoid unnecessary re-renders

  // Auto-select current project for admin when it changes
  useEffect(() => {
    if (currentProject && role !== 'superadmin') {
      // Admin: auto-select their current project
      setSelectedProjects([currentProject.id]);
    } else if (currentProject && role === 'superadmin') {
      // Superadmin: default to current project but allow selection
      if (!selectedProjects.includes(currentProject.id)) {
        setSelectedProjects([currentProject.id]);
      }
    }
  }, [currentProject, role]);

  // Update selected projects when projects list changes (new project added)
  useEffect(() => {
    if (role === 'superadmin' && projects && projects.length > 0 && currentProject) {
      // Ensure current project is in selected projects
      if (!selectedProjects.includes(currentProject.id)) {
        setSelectedProjects([currentProject.id]);
      }
    }
  }, [projects, currentProject, role]);

  const fetchVendors = async () => {
    try {
      const response = await vendorAPI.getAll();
      setVendorList(response.data || ['ALL VENDORS']);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      // Keep default 'ALL VENDORS' if fetch fails
      setVendorList(['ALL VENDORS']);
    }
  };

  const fetchBeneficiaries = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await beneficiariesAPI.getAll();
      const beneficiaries = (response.data || []).map(beneficiary => {
        // Parse assigned_vendors if it's a string
        if (beneficiary.assigned_vendors) {
          if (typeof beneficiary.assigned_vendors === 'string') {
            try {
              // Try to parse as JSON
              beneficiary.assigned_vendors = JSON.parse(beneficiary.assigned_vendors);
            } catch (e) {
              // If it's not valid JSON, treat it as a single string value
              beneficiary.assigned_vendors = [beneficiary.assigned_vendors];
            }
          }
          // Ensure it's an array
          if (!Array.isArray(beneficiary.assigned_vendors)) {
            beneficiary.assigned_vendors = [beneficiary.assigned_vendors];
          }
        } else {
          beneficiary.assigned_vendors = [];
        }
        return beneficiary;
      });
      setBeneficiaries(beneficiaries);
      setStatus(''); // Clear any previous errors
      console.log('✅ Beneficiaries fetched successfully:', beneficiaries.length);
    } catch (error) {
      console.error('❌ Error fetching beneficiaries:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      // Retry logic for production
      if (retryCount < 2) {
        console.log(`🔄 Retrying fetchBeneficiaries (attempt ${retryCount + 1})...`);
        setTimeout(() => fetchBeneficiaries(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      setStatus('خطأ في تحميل البيانات. يرجى تحديث الصفحة.');
      // Don't clear beneficiaries on error - keep existing data
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBeneficiary = async (beneficiary) => {
    if (!window.confirm(`هل أنت متأكد من حذف المستفيد "${beneficiary.name}" (${beneficiary.id_number})؟\n\nسيتم حذف جميع البيانات المرتبطة بهذا المستفيد بما في ذلك سجلات الاسترداد.`)) {
      return;
    }

    try {
      await beneficiariesAPI.delete(beneficiary.id);
      setStatus(`تم حذف المستفيد ${beneficiary.name} بنجاح.`);
      fetchBeneficiaries();
    } catch (error) {
      console.error("Error deleting beneficiary:", error);
      setStatus(error.response?.data?.error || 'فشل في حذف المستفيد.');
    }
  };

  const handleAddBeneficiary = async (e) => {
    e.preventDefault();
    setStatus('');
    setNewBeneficiary(null);

    // Validate project selection
    if (!selectedProjects || selectedProjects.length === 0) {
      setStatus('يرجى اختيار مشروع واحد على الأقل');
      return;
    }

    try {
      const response = await beneficiariesAPI.create({
        name,
        id_number: idNumber,
        phone,
        coupon_count: parseInt(couponCount, 10),
        coupon_value: parseFloat(couponValue) || 0,
        assigned_vendors: assignedVendors,
        project_ids: selectedProjects, // Send array of project IDs
      });

      if (response.data.success) {
        setStatus(`تم تسجيل المستفيد ${name} بنجاح مع ${couponCount} قسيمة!`);
        
        // Get the created beneficiary from response
        const createdBeneficiary = response.data.beneficiary;
        
        // If beneficiary is not in response, force immediate refresh
        if (!createdBeneficiary || !createdBeneficiary.id) {
          console.warn('Beneficiary not in response, forcing immediate refresh...');
          setTimeout(() => fetchBeneficiaries(), 100);
          return;
        }
        
        const uniqueId = createdBeneficiary.unique_beneficiary_id || crypto.randomUUID();
        const qrCodeUrl = `https://chart.googleapis.com/chart?cht=qr&chs=150x150&chl=${encodeURIComponent(`https://evoucher.sos-palestine.org/scan?key=${uniqueId}`)}`;
        
        setNewBeneficiary({
          name,
          id_number: idNumber,
          unique_beneficiary_id: uniqueId,
          coupon_count: parseInt(couponCount, 10),
          assigned_vendors: assignedVendors,
          qr_code_url: qrCodeUrl
        });
        
        // Clear form
        setName('');
        setIdNumber('');
        setPhone('');
        setCouponCount(1);
        setCouponValue(0);
        setAssignedVendors(['ALL VENDORS']);
        
        // Keep selected projects for superadmin, reset for admin
        if (role !== 'superadmin') {
          setSelectedProjects([currentProject.id]);
        }
        
        // Immediately add to list if beneficiary belongs to current project
        if (createdBeneficiary) {
          // Parse assigned_vendors if it's a string
          if (typeof createdBeneficiary.assigned_vendors === 'string') {
            try {
              createdBeneficiary.assigned_vendors = JSON.parse(createdBeneficiary.assigned_vendors);
            } catch (e) {
              createdBeneficiary.assigned_vendors = assignedVendors;
            }
          }
          if (!Array.isArray(createdBeneficiary.assigned_vendors)) {
            createdBeneficiary.assigned_vendors = assignedVendors;
          }
          
          // Ensure coupon_value is set
          if (!createdBeneficiary.coupon_value) {
            createdBeneficiary.coupon_value = parseFloat(couponValue) || 0;
          }
          
          // Check if beneficiary belongs to current project
          const belongsToCurrentProject = createdBeneficiary.project_ids 
            ? createdBeneficiary.project_ids.includes(currentProject.id)
            : selectedProjects.includes(currentProject.id);
          
          if (belongsToCurrentProject || role === 'superadmin') {
            // Add to the beginning of the list immediately
            setBeneficiaries(prev => {
              // Check if already exists to avoid duplicates
              const exists = prev.find(b => b.id === createdBeneficiary.id);
              if (exists) {
                return prev.map(b => b.id === createdBeneficiary.id ? createdBeneficiary : b);
              }
              return [createdBeneficiary, ...prev];
            });
          }
        }
        
        // Refresh vendors to ensure we have the latest vendor list
        fetchVendors();
        
        // Force immediate refresh to ensure we have the latest data from server
        // Use longer delay in production to ensure database commit
        setTimeout(() => {
          fetchBeneficiaries().catch(err => {
            console.error('Error refreshing beneficiaries after creation:', err);
            // Keep optimistic update even if refresh fails
          });
        }, 500);
      }
    } catch (error) {
      console.error('Error adding beneficiary:', error);
      setStatus(`فشل في تسجيل المستفيد: ${error.response?.data?.error || 'خطأ غير معروف'}`);
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setImportStatus('يرجى اختيار ملف CSV');
      return;
    }

    // For superadmin, show project selection dialog first
    if (role === 'superadmin' && projects && projects.length > 0) {
      setShowProjectSelection(true);
      return;
    }

    // For non-superadmin or if no projects, proceed with import
    await performImport();
  };

  const performImport = async () => {
    if (!csvFile) return;

    setImportStatus('بدء الاستيراد...');
    setIsImporting(true);
    setImportProgress(0);
    setImportDetails(null);
    setShowProjectSelection(false);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
    }, 500);

    try {
      // Set project context for import if superadmin selected a project
      const originalProjectId = localStorage.getItem('currentProjectId');
      if (role === 'superadmin' && selectedImportProject) {
        localStorage.setItem('currentProjectId', selectedImportProject.id.toString());
      }

      const response = await beneficiariesAPI.import(csvFile);
      
      // Restore original project context
      if (originalProjectId) {
        localStorage.setItem('currentProjectId', originalProjectId);
      }

      clearInterval(progressInterval);
      setImportProgress(100);
      
      const details = {
        successful: response.data.successful || 0,
        failed: response.data.failed || 0,
        errors: response.data.errors || []
      };
      
      setImportDetails(details);
      setImportStatus(`تم الاستيراد بنجاح: ${details.successful} نجح، ${details.failed} فشل`);
      setCsvFile(null);
      setSelectedImportProject(null);
      
      // Refresh beneficiaries list
      setTimeout(async () => {
        await fetchBeneficiaries();
        setImportProgress(0);
      }, 2000);
    } catch (error) {
      clearInterval(progressInterval);
      setImportProgress(0);
      console.error('Error importing CSV:', error);
      setImportStatus(`فشل في استيراد الملف: ${error.response?.data?.error || 'خطأ غير معروف'}`);
      setImportDetails(null);
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await beneficiariesAPI.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'beneficiaries.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setStatus('تم تصدير التقرير بنجاح!');
    } catch (error) {
      console.error('Error exporting:', error);
      setStatus('فشل في تصدير التقرير');
    }
  };

  const handleShowQR = (beneficiary) => {
    setSelectedQRBeneficiary(beneficiary);
    setShowQRModal(true);
  };

  const handlePrintQR = () => {
    window.print();
  };

  const fetchRedemptionDetails = async (beneficiaryId) => {
    if (expandedBeneficiaryId === beneficiaryId) {
      setExpandedBeneficiaryId(null);
      setRedemptionDetails({});
      return;
    }

    setIsFetchingDetails(true);
    setRedemptionDetails(prev => ({ ...prev, [beneficiaryId]: [] }));
    setExpandedBeneficiaryId(beneficiaryId);

    try {
      const response = await redemptionsAPI.getByBeneficiary(beneficiaryId);
      setRedemptionDetails(prev => ({ ...prev, [beneficiaryId]: response.data }));
    } catch (error) {
      console.error('Error fetching redemption details:', error);
      setRedemptionDetails(prev => ({ ...prev, [beneficiaryId]: null }));
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const generateReportNarrative = async () => {
    setIsGeneratingNarrative(true);
    setReportNarrative('');

    const totalAllocatedCoupons = beneficiaries.reduce((sum, b) => sum + b.coupon_count, 0);
    const totalRedeemedCoupons = beneficiaries.reduce((sum, b) => sum + b.redeemed_count, 0);
    const totalPendingCoupons = totalAllocatedCoupons - totalRedeemedCoupons;
    const redemptionRate = totalAllocatedCoupons > 0 ? (totalRedeemedCoupons / totalAllocatedCoupons) * 100 : 0;

    // Mock AI response
    const narrative = `تحليل برنامج القسائم الإلكترونية: يظهر البرنامج معدل استرداد ${redemptionRate.toFixed(1)}% مع ${totalRedeemedCoupons} قسيمة مستردة من أصل ${totalAllocatedCoupons} مخصصة. الميزانية المتبقية ${totalPendingCoupons} قسيمة توفر فرصة للتوسع في الخدمة. التوصية: التركيز على تحسين معدل الاسترداد من خلال توعية المستفيدين وتسهيل عملية الاسترداد.`;

    setTimeout(() => {
      setReportNarrative(narrative);
      setIsGeneratingNarrative(false);
    }, 2000);
  };

  // Calculate stats
  const totalAllocatedCoupons = beneficiaries.reduce((sum, b) => sum + b.coupon_count, 0);
  const totalRedeemedCoupons = beneficiaries.reduce((sum, b) => sum + b.redeemed_count, 0);
  const totalPendingCoupons = totalAllocatedCoupons - totalRedeemedCoupons;

  const chartData = [
    { name: 'مستردة', count: totalRedeemedCoupons, fill: '#10B981' },
    { name: 'معلقة', count: totalPendingCoupons, fill: '#F59E0B' },
  ];

  return (
    <div className="p-4 md:p-8">
      {editingBeneficiary && (
        <EditBeneficiaryModal 
          beneficiary={editingBeneficiary} 
          onClose={() => setEditingBeneficiary(null)} 
          onUpdate={fetchBeneficiaries}
          role={role}
        />
      )}
      
      <h2 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center">
        <BarChart3 className="w-8 h-8 mr-3 text-indigo-600" /> لوحة تحكم الإدارة
      </h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <p className="text-sm font-medium text-gray-500">إجمالي القسائم المخصصة</p>
          <p className="mt-1 text-4xl font-semibold text-gray-900">{totalAllocatedCoupons}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-emerald-500">
          <p className="text-sm font-medium text-gray-500">إجمالي المستردة</p>
          <p className="mt-1 text-4xl font-semibold text-emerald-600">{totalRedeemedCoupons}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-amber-500">
          <p className="text-sm font-medium text-gray-500">إجمالي المعلقة</p>
          <p className="mt-1 text-4xl font-semibold text-amber-600">{totalPendingCoupons}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">رسم بياني لحالة استرداد القسائم</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" stroke="#374151" />
            <YAxis allowDecimals={false} stroke="#374151" />
            <Tooltip
              contentStyle={{ borderRadius: '0.5rem', borderColor: '#D1D5DB', backgroundColor: '#fff' }}
              formatter={(value) => [`${value} قسيمة`, 'العدد']}
            />
            <Bar dataKey="count" radius={[10, 10, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>


      {/* AI Report Feature */}
      <div className="bg-violet-50 p-6 rounded-xl shadow-lg mb-8">
        <h3 className="text-2xl font-semibold text-violet-700 mb-4 flex items-center">
          <Sparkles className="w-6 h-6 mr-2" /> تقرير ذكي مدعوم بالذكاء الاصطناعي
        </h3>
        <button
          onClick={generateReportNarrative}
          disabled={isGeneratingNarrative || totalAllocatedCoupons === 0}
          className="flex items-center bg-violet-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-violet-700 transition duration-300 shadow-md disabled:opacity-50"
        >
          {isGeneratingNarrative ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Sparkles className="w-5 h-5 mr-2" />
          )}
          {isGeneratingNarrative ? 'جاري التحليل...' : '✨ إنشاء تقرير ذكي'}
        </button>

        {reportNarrative && (
          <div className="mt-4 p-4 bg-white border border-violet-300 rounded-lg shadow-inner">
            <p className="text-sm font-medium text-gray-700 mb-2">ملخص المحلل:</p>
            <p className="text-gray-800 italic leading-relaxed">{reportNarrative}</p>
          </div>
        )}
      </div>

      {/* CSV Import */}
      <div className="bg-red-50 p-6 rounded-xl shadow-lg mb-8">
        <h3 className="text-2xl font-semibold text-red-700 mb-4 flex items-center">
          <Upload className="w-5 h-5 mr-2" /> استيراد جماعي للمستفيدين (CSV)
        </h3>
        <p className="text-sm text-red-600 mb-3">
          <strong>تنسيق رؤوس CSV المطلوب:</strong> <span className="font-mono bg-red-100 p-1 rounded-md">NAME,ID_NUMBER,PHONE,COUPON_COUNT,COUPON_VALUE,VENDORS</span>
        </p>
        <p className="text-xs text-gray-600 mb-3">
          <strong>ملاحظة:</strong> COUPON_VALUE اختياري (افتراضي: 0). VENDORS يمكن أن يكون قائمة مفصولة بـ | مثل: vendor101|vendor102
        </p>
        <form onSubmit={handleImportCSV} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files[0])}
            className="md:col-span-3 p-3 border border-red-300 rounded-lg focus:ring-red-500 focus:border-red-500 transition duration-150"
            required
          />
          <button
            type="submit"
            disabled={isImporting || !csvFile}
            className="bg-red-600 text-white font-bold py-3 rounded-lg hover:bg-red-700 transition duration-300 shadow-md flex items-center justify-center disabled:opacity-50"
          >
            {isImporting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Upload className="w-5 h-5 mr-2" />} 
            {isImporting ? 'جاري الاستيراد...' : 'بدء الاستيراد'}
          </button>
        </form>
        
        {/* Project Selection Modal for Super Admin */}
        {showProjectSelection && role === 'superadmin' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">اختر المشروع</h3>
              <p className="text-gray-600 mb-4">يرجى اختيار المشروع الذي تريد إضافة المستفيدين إليه:</p>
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {projects && projects.length > 0 ? (
                  projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => setSelectedImportProject(project)}
                      className={`w-full text-right p-3 rounded-lg border-2 transition ${
                        selectedImportProject?.id === project.id
                          ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-semibold">{project.name}</div>
                      {project.description && (
                        <div className="text-sm text-gray-500 mt-1">{project.description}</div>
                      )}
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">لا توجد مشاريع متاحة</p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowProjectSelection(false);
                    setCsvFile(null);
                    setSelectedImportProject(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  إلغاء
                </button>
                <button
                  onClick={performImport}
                  disabled={!selectedImportProject}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  تأكيد والاستيراد
                </button>
              </div>
            </div>
          </div>
        )}
        
        {isImporting && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">جاري الاستيراد...</span>
              <span className="text-sm font-medium text-gray-700">{importProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${importProgress}%` }}
              ></div>
            </div>
          </div>
        )}
        {importStatus && !isImporting && (
          <div className={`mt-4 p-3 rounded-lg font-medium ${
            importStatus.includes('نجح') || importStatus.includes('نجاح') 
              ? 'bg-green-200 text-green-800' 
              : 'bg-red-200 text-red-800'
          }`}>
            {importStatus}
            {importDetails && (
              <div className="mt-2 text-sm">
                <p>✅ نجح: {importDetails.successful}</p>
                {importDetails.failed > 0 && (
                  <>
                    <p>❌ فشل: {importDetails.failed}</p>
                    {importDetails.errors && importDetails.errors.length > 0 && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                        <strong>تفاصيل الأخطاء:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {importDetails.errors.slice(0, 5).map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manual Registration */}
      <div className="bg-indigo-50 p-6 rounded-xl shadow-lg mb-8">
        <h3 className="text-2xl font-semibold text-indigo-700 mb-4 flex items-center">
          <UserPlus className="w-5 h-5 mr-2" /> تسجيل مستفيد يدوي
        </h3>
        <form onSubmit={handleAddBeneficiary} className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-indigo-700 mb-2">الاسم الكامل للمستفيد *</label>
            <input
              type="text"
              placeholder="أدخل الاسم الكامل"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 border border-indigo-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-indigo-700 mb-2">رقم الهوية الوطنية *</label>
            <input
              type="text"
              placeholder="أدخل رقم الهوية"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value.toUpperCase())}
              className="w-full p-3 border border-indigo-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
              required
            />
          </div>
          
          {/* Project Selection - Show for superadmin */}
          {role === 'superadmin' && (
            <div className="md:col-span-5 mt-2 mb-2">
              <label className="block text-sm font-semibold text-indigo-700 mb-3 flex items-center">
                <span className="mr-2 text-lg">📁</span>
                اختيار المشاريع (يمكن اختيار أكثر من مشروع) <span className="text-red-500 mr-1">*</span>
              </label>
              {projects && projects.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {projects.map((project) => (
                      <label 
                        key={project.id} 
                        className={`flex items-center space-x-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedProjects.includes(project.id)
                            ? 'border-indigo-500 bg-indigo-100 shadow-md'
                            : 'border-gray-300 hover:border-indigo-300 hover:bg-indigo-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProjects([...selectedProjects, project.id]);
                            } else {
                              setSelectedProjects(selectedProjects.filter(id => id !== project.id));
                            }
                          }}
                          className="w-5 h-5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">{project.name}</span>
                      </label>
                    ))}
                  </div>
                  {selectedProjects.length === 0 && (
                    <p className="text-xs text-red-600 mt-2 font-semibold">⚠️ يرجى اختيار مشروع واحد على الأقل</p>
                  )}
                  {selectedProjects.length > 0 && (
                    <p className="text-xs text-green-600 mt-2">
                      ✓ تم اختيار {selectedProjects.length} مشروع
                    </p>
                  )}
                </>
              ) : (
                <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    لا توجد مشاريع متاحة. يرجى إنشاء مشاريع من قائمة إدارة المستخدمين أولاً.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Show current project for admin (read-only) */}
          {role !== 'superadmin' && currentProject && (
            <div className="md:col-span-5 mt-2 mb-2 p-3 bg-indigo-100 border border-indigo-300 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                المشروع الحالي:
              </label>
              <span className="text-base font-bold text-indigo-700">{currentProject.name}</span>
              {currentProject.description && (
                <p className="text-xs text-gray-600 mt-1">{currentProject.description}</p>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-semibold text-indigo-700 mb-2">عدد القسائم *</label>
            <input
              type="number"
              placeholder="أدخل عدد القسائم"
              value={couponCount}
              min="1"
              onChange={(e) => setCouponCount(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full p-3 border border-indigo-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-indigo-700 mb-2">قيمة القسيمة</label>
            <input
              type="number"
              step="0.01"
              placeholder="أدخل قيمة القسيمة (اختياري)"
              value={couponValue}
              min="0"
              onChange={(e) => setCouponValue(parseFloat(e.target.value) || 0)}
              className="w-full p-3 border border-indigo-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-indigo-700 mb-2">رقم الهاتف *</label>
            <input
              type="tel"
              placeholder="أدخل رقم الهاتف"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-3 border border-indigo-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
              required
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">تعيين البائعين</label>
            <VendorAssignmentSelect 
              selectedVendors={assignedVendors} 
              onChange={setAssignedVendors}
              vendorList={vendorList}
            />
          </div>
          <button
            type="submit"
            className="md:col-span-5 bg-indigo-600 text-white font-bold py-3 rounded-lg hover:bg-indigo-700 transition duration-300 shadow-md"
          >
            تسجيل المستفيد وإنشاء QR
          </button>
        </form>
        {status && (
          <div className="mt-4 p-3 bg-indigo-200 text-indigo-800 rounded-lg font-medium">{status}</div>
        )}

        {newBeneficiary && (
          <div className="mt-6 p-4 bg-white border border-green-300 rounded-lg flex flex-col md:flex-row items-center justify-between shadow-inner">
            <div className="md:pr-4">
              <p className="text-lg font-bold text-green-600">تم تسجيل المستفيد!</p>
              <p>الاسم: <span className="font-semibold">{newBeneficiary.name}</span></p>
              <p>القسائم: <span className="font-semibold">{newBeneficiary.coupon_count}</span></p>
              <p className="text-sm text-gray-700 mt-1">مُعين لـ: <span className="font-semibold text-indigo-700">{newBeneficiary.assigned_vendors.join(', ')}</span></p>
            </div>
            <div className="flex flex-col items-center space-y-3 mt-4 md:mt-0">
              <p className="text-sm text-gray-500 font-medium">رمز QR قابل للمسح</p>
              <QRCodeHighQuality 
                uniqueId={newBeneficiary.unique_beneficiary_id} 
                size={120}
              />
            </div>
          </div>
        )}
      </div>

      {/* Beneficiaries List */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-semibold text-gray-800 flex items-center">
            <List className="w-5 h-5 mr-2 text-gray-600" /> جميع المستفيدين
          </h3>
          <button
            onClick={handleExport}
            className="flex items-center bg-green-500 text-white py-2 px-4 rounded-lg text-sm hover:bg-green-600 transition duration-300 shadow-md"
          >
            <Download className="w-4 h-4 mr-2" /> تصدير التقرير (CSV)
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" />
            <p className="text-gray-500 mt-2">جاري تحميل البيانات...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Search Bar */}
            <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="ابحث عن مستفيد (الاسم، رقم الهوية، الهاتف، المشروع)..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    مسح البحث
                  </button>
                )}
              </div>
            </div>
            
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">الاسم</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">رقم الهوية</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">اسم المشروع</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">البائعون المعينون</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">القسائم (إجمالي)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">قيمة القسيمة</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">القسائم (مستردة)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // Filter beneficiaries based on search query
                  const filteredBeneficiaries = beneficiaries.filter(b => {
                    if (!searchQuery.trim()) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      b.name?.toLowerCase().includes(query) ||
                      b.id_number?.toLowerCase().includes(query) ||
                      b.phone?.toLowerCase().includes(query) ||
                      b.project_names?.toLowerCase().includes(query)
                    );
                  });
                  
                  // Calculate pagination
                  const totalPages = Math.ceil(filteredBeneficiaries.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedBeneficiaries = filteredBeneficiaries.slice(startIndex, endIndex);
                  
                  // Reset to page 1 if current page is out of bounds
                  if (currentPage > totalPages && totalPages > 0) {
                    setTimeout(() => setCurrentPage(1), 0);
                  }
                  
                  return paginatedBeneficiaries.map((beneficiary) => (
                  <React.Fragment key={beneficiary.id}>
                    <tr className={beneficiary.redeemed_count > 0 ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{beneficiary.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{beneficiary.id_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {beneficiary.project_names ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            {beneficiary.project_names}
                          </span>
                        ) : (
                          <span className="text-gray-400">غير محدد</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-indigo-600">
                        {beneficiary.assigned_vendors && Array.isArray(beneficiary.assigned_vendors) && beneficiary.assigned_vendors.length > 0
                          ? beneficiary.assigned_vendors.join(', ')
                          : 'غير محدد'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{beneficiary.coupon_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-semibold">
                        {(beneficiary.coupon_value || 0).toLocaleString()}
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold cursor-pointer"
                        onClick={() => beneficiary.redeemed_count > 0 && fetchRedemptionDetails(beneficiary.id)}
                      >
                        {beneficiary.redeemed_count}
                        {beneficiary.redeemed_count > 0 && (
                          <span className="ml-2 text-indigo-600 underline text-xs font-semibold">
                            ({beneficiary.id === expandedBeneficiaryId ? 'إخفاء' : 'عرض'})
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {beneficiary.redeemed_count === beneficiary.coupon_count ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            مكتمل
                          </span>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${beneficiary.redeemed_count > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}`}>
                            {beneficiary.redeemed_count > 0 ? 'قيد التنفيذ' : 'معلق'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingBeneficiary(beneficiary)}
                            className="text-indigo-600 hover:text-indigo-900 flex items-center text-xs font-bold p-1 rounded-md bg-indigo-100 hover:bg-indigo-200 transition"
                          >
                            <Pencil className="w-4 h-4 mr-1"/> تعديل
                          </button>
                          <button
                            onClick={() => handleShowQR(beneficiary)}
                            className="text-green-600 hover:text-green-900 flex items-center text-xs font-bold p-1 rounded-md bg-green-100 hover:bg-green-200 transition"
                          >
                            <QrCode className="w-4 h-4 mr-1"/> QR
                          </button>
                          {(role === 'superadmin' || role === 'admin') && (
                            <button
                              onClick={() => handleDeleteBeneficiary(beneficiary)}
                              className="text-red-600 hover:text-red-900 flex items-center text-xs font-bold p-1 rounded-md bg-red-100 hover:bg-red-200 transition"
                              title="حذف المستفيد"
                            >
                              <Trash2 className="w-4 h-4 mr-1"/> حذف
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Row for Redemption Details */}
                    {beneficiary.id === expandedBeneficiaryId && (
                      <tr>
                        <td colSpan={(role === 'superadmin' || role === 'admin') ? 8 : 7} className="px-6 py-4 bg-gray-100 border-t border-indigo-200">
                          <h4 className="text-md font-semibold mb-3 text-indigo-700">سجل التدقيق: معرفات القسائم الفريدة ({beneficiary.redeemed_count})</h4>
                          {isFetchingDetails && redemptionDetails[beneficiary.id]?.length === 0 ? (
                            <div className="flex items-center text-indigo-500">
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> جاري التحميل...
                            </div>
                          ) : redemptionDetails[beneficiary.id] && redemptionDetails[beneficiary.id].length > 0 ? (
                            <ul className="space-y-2 max-h-40 overflow-y-auto pr-2">
                              {redemptionDetails[beneficiary.id].map((redemption, index) => (
                                <li key={redemption.unique_coupon_id} className="p-2 bg-white rounded-lg shadow-sm text-sm border-l-4 border-violet-400 font-mono text-gray-800 flex justify-between items-center">
                                  <span className="truncate">
                                    {index + 1}. **{redemption.unique_coupon_id}**
                                  </span>
                                  <span className="text-xs text-gray-500 ml-4 flex-shrink-0">
                                    البائع: {redemption.redeemed_by_vendor_id} | {new Date(redemption.redemption_timestamp).toLocaleDateString()}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-red-500">فشل في تحميل التفاصيل أو لا توجد سجلات.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ));
                })()}
                {(() => {
                  const filteredBeneficiaries = beneficiaries.filter(b => {
                    if (!searchQuery.trim()) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      b.name?.toLowerCase().includes(query) ||
                      b.id_number?.toLowerCase().includes(query) ||
                      b.phone?.toLowerCase().includes(query) ||
                      b.project_names?.toLowerCase().includes(query)
                    );
                  });
                  
                  if (filteredBeneficiaries.length === 0) {
                    return (
                      <tr>
                        <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                          {searchQuery ? 'لم يتم العثور على مستفيدين يطابقون البحث.' : 'لم يتم تسجيل مستفيدين بعد.'}
                        </td>
                      </tr>
                    );
                  }
                  return null;
                })()}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {(() => {
              const filteredBeneficiaries = beneficiaries.filter(b => {
                if (!searchQuery.trim()) return true;
                const query = searchQuery.toLowerCase();
                return (
                  b.name?.toLowerCase().includes(query) ||
                  b.id_number?.toLowerCase().includes(query) ||
                  b.phone?.toLowerCase().includes(query) ||
                  b.project_names?.toLowerCase().includes(query)
                );
              });
              
              const totalPages = Math.ceil(filteredBeneficiaries.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = Math.min(startIndex + itemsPerPage, filteredBeneficiaries.length);
              
              if (totalPages <= 1) return null;
              
              return (
                <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 border-t border-gray-200 rounded-b-lg">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      عرض {startIndex + 1} إلى {endIndex} من أصل {filteredBeneficiaries.length} مستفيد
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <ChevronRight className="w-4 h-4" />
                      السابق
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                page === currentPage
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 3 ||
                          page === currentPage + 3
                        ) {
                          return <span key={page} className="px-2 text-gray-500">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      التالي
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && selectedQRBeneficiary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">QR Code للاسترداد</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePrintQR}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center"
                  >
                    <Printer className="w-4 h-4 mr-2"/> طباعة
                  </button>
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                  >
                    إغلاق
                  </button>
                </div>
              </div>
              
              <div className="text-center mb-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                  {selectedQRBeneficiary.name}
                </h4>
                <p className="text-sm text-gray-600">
                  {selectedQRBeneficiary.phone} • {selectedQRBeneficiary.coupon_count} قسيمة
                </p>
              </div>
              
              <div className="flex justify-center">
                <QRCodePrintable 
                  uniqueId={selectedQRBeneficiary.unique_beneficiary_id}
                  size={400}
                  showDetails={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
